import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from '../../../global-components/src';
import axios from "axios";
import "./style.css";
import { useMessage } from "../../../global-components/src/index";
import { BookPlaceholder } from "../placeholder";
import { READBOOKS_API_URL } from "../../../global-components/src/utils/vars";
import { censorText, clearText, errorMessage } from "../../../global-components/src/utils/text";
import { BookOpenText, BookMarked, PenLine, Users, BookCheck, Heart, LayoutDashboard } from "lucide-react";
import Spinner from "../../../global-components/src/components/spinner/spiner";
import { getBookImage } from "../../../global-components/src/utils/book";
import { Book, User } from "../../../global-components/src/types";
import NotFound from "../../errors/NotFound";
import { getImageUser } from "../../../global-components/src/utils/auth";
import ImgVerified from "../../../global-components/src/components/img-verified";
import ReadingDashboardPage from "../../dashboard/dashboard";

// ─── tipos ───────────────────────────────────────────────────────────────────

// No topo, adicionar tipo
interface Recommendation {
    source: 'catalog' | 'google';
    uuid: string;
    title: string;
    author: string;
    cover_url?: string;
    price?: number;
    description?: string;
}

interface CatalogBook {
    uuid: string;
    title: string;
    author: string;
    cover_url: string;
    description?: string;
    price?: number;
    status: string;
}

// ─── helpers ─────────────────────────────────────────────────────────────────

function Avatar({ src, name, size = 96 }: { src?: string; name: string; size?: number }) {
    const [errored, setErrored] = useState(false);
    const initials = name
        .split(" ")
        .slice(0, 2)
        .map((w) => w[0])
        .join("")
        .toUpperCase();

    if (src && !errored) {
        return (
            <img
                src={getImageUser(src)}
                alt={name}
                width={size}
                height={size}
                style={{
                    width: size,
                    height: size,
                    borderRadius: "50%",
                    objectFit: "cover",
                    border: "3px solid var(--color-accent, #5bb4ff)",
                    flexShrink: 0,
                }}
                onError={() => setErrored(true)}
            />
        );
    }

    return (
        <div
            style={{
                width: size,
                height: size,
                borderRadius: "50%",
                background: "linear-gradient(135deg, #5bb4ff 0%, #3a8fd4 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: size * 0.38,
                fontWeight: 700,
                color: "#fff",
                flexShrink: 0,
                border: "3px solid var(--color-accent, #5bb4ff)",
            }}
        >
            {initials}
        </div>
    );
}

function StatBadge({ label, value }: { label: string; value: number | string }) {
    return (
        <div style={{ textAlign: "center", minWidth: 64 }}>
            <div style={{ fontSize: 18, fontWeight: 700, lineHeight: 1.2 }}>{value}</div>
            <div style={{ fontSize: 12, opacity: 0.6, marginTop: 2 }}>{label}</div>
        </div>
    );
}

// ─── componente principal ─────────────────────────────────────────────────────

export function Books() {
    const [filter, setfilter] = useState<"all" | "read" | "not_read" | "wish_list">("all");
    const [searchParams] = useSearchParams();
    const [totalReadBooks, setTotalReadBooks] = useState(0);
    const [books, setBooks] = useState<Book[] | null>(null);
    const [publishedBooks, setPublishedBooks] = useState<CatalogBook[] | null>(null);
    const [errorLoading, setErrorLoading] = useState<number | null>(null);
    const [totalBooks, setTotalBooks] = useState(0);
    const [totalWishList, setTotalWishList] = useState(0);
    const [totalNotReadBooks, setTotalNotReadBooks] = useState(0);
    const [loading, setLoading] = useState(false);
    const [loadingSearch, setLoadingSearch] = useState(false);
    const [loadingPublished, setLoadingPublished] = useState(false);
    const [profileUser, setProfileUser] = useState<User | null>(null);
    const [title, setTitle] = useState("");
    const [activeTab, setActiveTab] = useState<any>("dashboard");
    const { user } = useAuth();
    const [end, setEnd] = useState(false);
    const { token } = useAuth();
    const [page, setPage] = useState(1);
    const endOfListRef = useRef<HTMLDivElement | null>(null);
    const { setMessage } = useMessage();
    const [termo, setTermo] = useState(searchParams.get("termo") || "");
    const [contextMenu, setContextMenu] = useState<any | null>({});
    const navigate = useNavigate();
    const [userName, setUserName] = useState<string | null>(null);
    const [isNotFoundPage, setIsNotFoundPage] = useState(false);
    const isFirstLoading = useRef(true);
    // Junto aos outros estados
    const [recommendations, setRecommendations] = useState<Recommendation[] | null>(null);
    const [loadingRecs, setLoadingRecs] = useState(false);

    // Nova função de carregamento
    const loadRecommendations = async () => {
        if (loadingRecs || userName) return; // só para usuário logado
        setLoadingRecs(true);
        try {
            const response = await axios.get(`${READBOOKS_API_URL}/catalog/recommendations`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setRecommendations(response.data.recommendations ?? []);
        } catch {
            setRecommendations([]);
        } finally {
            setLoadingRecs(false);
        }
    };

    useEffect(() => {
        if (!userName && activeTab === "dashboard") {
            loadRecommendations();
        }
    }, [activeTab, userName]);

    // ── event listeners ───────────────────────────────────────────────────────

    useEffect(() => {
        const closeMenu = () => setContextMenu(null);
        window.addEventListener("click", closeMenu);
        return () => window.removeEventListener("click", closeMenu);
    }, []);

    useEffect(() => {
        const path = window.location.pathname;
        if (window.location.pathname !== "/livros") {
            const match = path.match(/^\/@(.+)/);
            if (match) {
                setUserName(match[1]);
            } else {
                setIsNotFoundPage(true);
                return;
            }
        }
    }, [navigate]);

    // ── context menu ──────────────────────────────────────────────────────────

    const handleContextMenu = (e: any, uuid: string) => {
        e.preventDefault();
        setContextMenu({ x: e.pageX, y: e.pageY, uuid });
    };

    // ── data loading ──────────────────────────────────────────────────────────

    const loadData = async (page: number) => {
        const storageKey = userName ?? user?.user_name;

        localStorage.removeItem(`books-${user?.id}`);
        localStorage.removeItem(`total-books-${user?.id}`);
        localStorage.removeItem(`total-read-books-${user?.id}`);
        localStorage.removeItem(`total-wish-list-${user?.id}`);
        localStorage.removeItem(`total-not-read-books-${user?.id}`);

        // carrega cache local
        const stored = localStorage.getItem(`books-${storageKey}`);
        if (!isNaN(Number(localStorage.getItem(`total-books-${storageKey}`)))) setTotalBooks(Number(localStorage.getItem(`total-books-${storageKey}`)));
        if (!isNaN(Number(localStorage.getItem(`total-read-books-${storageKey}`)))) setTotalReadBooks(Number(localStorage.getItem(`total-read-books-${storageKey}`)));
        if (!isNaN(Number(localStorage.getItem(`total-wish-list-${storageKey}`)))) setTotalWishList(Number(localStorage.getItem(`total-wish-list-${storageKey}`)));
        if (!isNaN(Number(localStorage.getItem(`total-not-read-books-${storageKey}`)))) setTotalNotReadBooks(Number(localStorage.getItem(`total-not-read-books-${storageKey}`)));
        if (stored) setBooks(JSON.parse(stored));

        if (loading || end) return;
        setLoading(true);

        try {
            const url = userName
                ? `${READBOOKS_API_URL}/books/by-username/${userName}?page=${page}`
                : `${READBOOKS_API_URL}/books?page=${page}`;

            const response = await axios.get(url, {
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                    Authorization: `Bearer ${token}`,
                },
            });

            isFirstLoading.current = false;
            const data = response.data;

            // guarda dados de perfil vindos da resposta
            if (data?.user) {
                setProfileUser(data.user);
            }

            if (data?.books?.length > 0) {
                setBooks((prev) => {
                    const current = prev ?? [];
                    const newBooks = data.books.filter(
                        (b: { id_livro: number }) => !current.some((c) => c.id_livro === b.id_livro)
                    );
                    return [...current, ...newBooks];
                });

                data.books.forEach((book: Book) => {
                    localStorage.setItem(`book-${book.uuid}`, JSON.stringify(book));
                    const clean = clearText(censorText(book.descricao_livro));
                    localStorage.setItem(`processedText-${book.uuid}`, clean);
                });

                localStorage.setItem(`books-${storageKey}`, JSON.stringify(data.books));
                localStorage.setItem(`total-books-${storageKey}`, data.totalBooks);
                localStorage.setItem(`total-read-books-${storageKey}`, data.totalReadBooks);
                localStorage.setItem(`total-wish-list-${storageKey}`, data.totalWishList);
                localStorage.setItem(`total-not-read-books-${storageKey}`, data.totalNotReadBooks);

                setTotalBooks(data.totalBooks);
                setTotalReadBooks(data.totalReadBooks);
                setTotalWishList(data.totalWishList);
                setTotalNotReadBooks(data.totalNotReadBooks);
            } else {
                setEnd(true);
                setPage(1);
            }
        } catch (err: any) {
            if (err?.response?.status !== 404) {
                const msg = errorMessage(err);
                setMessage(msg, "error");
                setErrorLoading(msg);
            }
        } finally {
            setLoading(false);
        }
    };

    // carrega livros publicados quando total_books_publish > 0
    const loadPublishedBooks = async () => {
        if (!userName) return;
        setLoadingPublished(true);
        try {
            const response = await axios.get(
                `${READBOOKS_API_URL}/catalog/by-user-id/${displayProfile?.id}`,
                {
                    headers: {
                        "Content-Type": "application/json",
                        Accept: "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            
            setPublishedBooks(response.data?.books.data ?? []);
        } catch {
            setPublishedBooks([]);
        } finally {
            setLoadingPublished(false);
        }
    };

    useEffect(() => {
        if (
            (userName && window.location.pathname !== "/livros") ||
            (!userName && window.location.pathname === "/livros")
        ) {
            loadData(1);
        }
    }, [userName]);

    // carrega livros publicados quando temos o perfil e ele tem publicações
    useEffect(() => {
        if (profileUser?.id && (profileUser.total_books_publish ?? 0) > 0) {
            loadPublishedBooks();
        }
    }, [profileUser]);

    useEffect(() => {
        setTitle(userName ? `Livros de @${userName}` : "Meus livros");
    }, [user, userName]);

    useEffect(() => {
        setPage(1);
        if (termo) handleSearch(1);
    }, [filter]);

    // ── handlers ──────────────────────────────────────────────────────────────

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setPage(1);
        setTermo(event.target.value);
    };

    const handleSearch = async (newPage = 1) => {
        if (loadingSearch) return;
        setLoadingSearch(true);
        try {
            const url = `${READBOOKS_API_URL}/books${userName ? `/by-username/${userName}` : ""}?page=${newPage}&search=${encodeURIComponent(termo)}&filter=${filter}`;
            const response = await axios.get(url, {
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                    Authorization: `Bearer ${token}`,
                },
            });
            const data = response.data;
            if (page === 1) setBooks(null);
            if (data?.books?.length > 0) {
                setBooks((prev) => [...(prev ?? []), ...data.books]);
                setTotalBooks(data.totalBooks);
                setTotalReadBooks(data.totalReadBooks);
                setTotalWishList(data.totalWishList);
                setTotalNotReadBooks(data.totalNotReadBooks);
            } else {
                setEnd(true);
                setPage(1);
            }
        } catch (err: any) {
            const msg = errorMessage(err);
            setMessage(msg, "error");
        } finally {
            setLoadingSearch(false);
        }
    };

    const getTitle = () => {
        if (filter === "read") return `${title} - finalizados`;
        if (filter === "not_read") return `${title} - não finalizados`;
        if (filter === "wish_list") return `${title} - lista de desejo`;
        return title;
    };

    // ── guards ────────────────────────────────────────────────────────────────

    if (isNotFoundPage) return <NotFound />;
    if (errorLoading === 404)
        return <NotFound children=" " title="404" showLink={false} text="Usuário não encontrado" />;

    

    // ── derived ───────────────────────────────────────────────────────────────

    const hasPublishedBooks = (profileUser?.total_books_publish ?? 0) > 0;

    // perfil a mostrar: se estamos em /@username usamos profileUser, senão usamos o user logado
    const displayProfile: User | null = userName
        ? profileUser
        : user
        ? null
        : null;

    const tabs = userName
        ? (hasPublishedBooks
            ? [
                { key: "dashboard", label: "Dashboard",    icon: <LayoutDashboard size={15} /> },
                { key: "read",      label: "Livros lidos",  icon: <BookMarked size={15} /> },
                { key: "published", label: "Publicações",   icon: <PenLine size={15} /> },
            ]
            : [
                { key: "dashboard", label: "Dashboard",    icon: <LayoutDashboard size={15} /> },
                { key: "read",      label: "Livros lidos",  icon: <BookMarked size={15} /> },
            ])
        : [
            { key: "dashboard", label: "Recomendações",  icon: <BookOpenText size={15} /> },
            { key: "read",      label: "Meus livros",    icon: <BookMarked size={15} /> },
        ];

    // ── render ────────────────────────────────────────────────────────────────

    return (
        <div className="container container-livros">

            {/* ── PERFIL ──────────────────────────────────────────────────── */}
            {displayProfile && (
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 12,
                        marginBottom: 32,
                        paddingTop: 16,
                    }}
                >
                    <Avatar
                        src={displayProfile.img_account}
                        name={displayProfile.name}
                        size={96}
                    />

                    <div style={{ textAlign: "center" }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                            <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>
                                {displayProfile.name}
                            </h2>
                            {!!displayProfile.verified_profile && (
                                <span
                                    title="Perfil verificado"
                                    style={{ color: "#5bb4ff", fontSize: 18, lineHeight: 1 }}
                                >
                                    <ImgVerified user={displayProfile} classNameProp="user-select-none img-verificado-conta"/>
                                </span>
                            )}
                        </div>
                        <div style={{ opacity: 0.55, fontSize: 14 }}>@{displayProfile.user_name}</div>
                    </div>

                    {displayProfile.biography && (
                        <p
                            style={{
                                margin: 0,
                                maxWidth: 480,
                                textAlign: "center",
                                fontSize: 14,
                                opacity: 0.75,
                                lineHeight: 1.5,
                            }}
                        >
                            {displayProfile.biography}
                        </p>
                    )}

                    {/* estatísticas do perfil */}
                    <div
                        style={{
                            display: "flex",
                            gap: 24,
                            marginTop: 4,
                            flexWrap: "wrap",
                            justifyContent: "center",
                        }}
                    >
                        {displayProfile.total_followers !== undefined && (
                            <StatBadge label="Seguidores" value={displayProfile.total_followers} />
                        )}
                        {displayProfile.total_following !== undefined && (
                            <StatBadge label="Seguindo" value={displayProfile.total_following} />
                        )}
                        {displayProfile.total_posts !== undefined && (
                            <StatBadge label="Posts" value={displayProfile.total_posts} />
                        )}
                        {displayProfile.total_poems !== undefined && (
                            <StatBadge label="Poemas" value={displayProfile.total_poems} />
                        )}
                    </div>
                </div>
            )}

            {/* ── ESTATÍSTICAS DE LEITURA ─────────────────────────────────── */}
            {userName && (
                <div
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        gap: 16,
                        flexWrap: "wrap",
                        marginBottom: 28,
                    }}
                >
                    <div className="stat-card">
                        <BookCheck size={18} style={{ marginRight: 6, color: "#5bb4ff" }} />
                        <strong>{totalReadBooks}</strong>&nbsp;finalizados
                    </div>
                    <div className="stat-card">
                        <BookMarked size={18} style={{ marginRight: 6, color: "#f59e0b" }} />
                        <strong>{totalNotReadBooks}</strong>&nbsp;não finalizados
                    </div>
                    <div className="stat-card">
                        <Heart size={18} style={{ marginRight: 6, color: "#f43f5e" }} />
                        <strong>{totalWishList}</strong>&nbsp;lista de desejo
                    </div>
                    {hasPublishedBooks && (
                        <div className="stat-card">
                            <PenLine size={18} style={{ marginRight: 6, color: "#8b5cf6" }} />
                            <strong>{profileUser!.total_books_publish}</strong>&nbsp;publicados
                        </div>
                    )}
                </div>
            )}


            {/* ── ABAS (só aparecem quando há livros publicados) ───────────── */}
                {userName && (
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "center",
                            gap: 4,
                            marginBottom: 28,
                            borderBottom: "2px solid rgba(0,0,0,0.08)",
                        }}
                    >
                        {(
                            tabs
                        ).map((tab) => (
                            <button
                                key={tab.key}
                                onClick={() => setActiveTab(tab.key)}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 6,
                                    padding: "10px 20px",
                                    border: "none",
                                    background: "transparent",
                                    cursor: "pointer",
                                    fontWeight: activeTab === tab.key ? 700 : 400,
                                    borderBottom:
                                        activeTab === tab.key
                                            ? "3px solid #5bb4ff"
                                            : "3px solid transparent",
                                    color: activeTab === tab.key ? "#5bb4ff" : "inherit",
                                    marginBottom: -2,
                                    transition: "all 0.2s",
                                }}
                            >
                                {tab.icon}
                                {tab.label}
                            </button>
                        ))}
                    </div>
                )}

            {/* ── PESQUISA & FILTRO (só na aba de livros lidos) ───────────── */}
            {activeTab === "read" && (
                <div className="row row-pesquisa-livro" style={{ marginBottom: 36 }}>
                    <div className="col">
                        <form
                            className="d-flex"
                            onSubmit={(e) => {
                                e.preventDefault();
                                handleSearch();
                            }}
                        >
                            <input
                                value={termo}
                                onChange={handleChange}
                                className="form-control me-2"
                                type="search"
                                id="livro"
                                name="livro"
                                placeholder="Pesquisar livros"
                                aria-label="Search"
                            />
                            <button
                                disabled={loadingSearch}
                                className="btn"
                                style={{
                                    background: "#5bb4ff",
                                    color: "#fff",
                                    padding: 0,
                                    minWidth: 50,
                                }}
                                type="submit"
                            >
                                {loadingSearch ? (
                                    <Spinner className="text-light" size="25px" />
                                ) : (
                                    <img height="40px" src="/img/search.png" alt="Pesquisar" />
                                )}
                            </button>
                        </form>

                        <select
                            name="filtro"
                            className="form-select mt-4"
                            aria-label="Filtrar livros"
                            onChange={(e) =>
                                setfilter(e.target.value as "all" | "read" | "not_read" | "wish_list")
                            }
                            value={filter}
                        >
                            <option value="all">Todos os livros</option>
                            <option value="read">Lidos</option>
                            <option value="not_read">Não lidos</option>
                            <option value="wish_list">Lista de desejo</option>
                        </select>
                    </div>
                </div>
            )}

            {/* ── GRID DE LIVROS LIDOS ─────────────────────────────────────── */}
            {activeTab === "read" && (
                <>
                    <div className="row row-list-livros">
                        {books?.length
                            ? books.map((book, index) => (
                                  <div
                                      key={index}
                                      className="col col-livro"
                                      onContextMenu={(e) => handleContextMenu(e, book.uuid)}
                                  >
                                      <Link className="link-livro" to={"/livros/" + book.uuid}>
                                          <img
                                              onError={(e) => {
                                                  e.currentTarget.src = "/img/book_transparent.png";
                                              }}
                                              src={getBookImage(book?.img_livro)}
                                              className="livro"
                                              alt={book.nome_livro}
                                          />
                                      </Link>
                                  </div>
                              ))
                            : null}

                        {loading && (
                            <BookPlaceholder className="col col-livro" quantity={12} />
                        )}
                    </div>

                    {!books?.length && !loading && (
                        <div className="text-center" style={{ opacity: 0.6 }}>
                            <BookOpenText className="icon-empty" />
                            <h3 className="w-100 text-center user-select-none">
                                {errorLoading ? errorLoading : "Nenhum livro cadastrado"}
                            </h3>
                        </div>
                    )}
                </>
            )}

            {/* ── GRID DE LIVROS PUBLICADOS ────────────────────────────────── */}
            {activeTab === "published" && (
                <>
                    <div className="row row-list-livros">
                        {publishedBooks?.length
                            ? publishedBooks.map((book, index) => (
                                  <div key={index} className="col col-livro">
                                      <Link
                                          className="link-livro"
                                          to={`/catalogo/${book.uuid}`}
                                          title={book.title}
                                      >
                                          <img
                                              onError={(e) => {
                                                  e.currentTarget.src = "/img/book_transparent.png";
                                              }}
                                              src={
                                                  book.cover_url
                                                      ? getBookImage(book.cover_url)
                                                      : "/img/book_transparent.png"
                                              }
                                              className="livro"
                                              alt={book.title}
                                          />
                                      </Link>
                                  </div>
                              ))
                            : null}

                        {loadingPublished && (
                            <BookPlaceholder className="col col-livro" quantity={6} />
                        )}
                    </div>

                    {!publishedBooks?.length && !loadingPublished && (
                        <div className="text-center" style={{ opacity: 0.6 }}>
                            <PenLine
                                size={48}
                                className="icon-empty"
                                style={{ display: "block", margin: "0 auto 12px" }}
                            />
                            <h3 className="w-100 text-center user-select-none">
                                Nenhum livro publicado
                            </h3>
                        </div>
                    )}
                </>
            )}

            {activeTab === "dashboard" && !userName && (
                <div>
                    <h3 style={{ textAlign: "center", marginBottom: 24, opacity: 0.7, fontWeight: 600 }}>
                        Recomendados para você
                    </h3>
                    <div className="row row-list-livros">
                        {recommendations?.map((book, index) => (
                            <div key={index} className="col col-livro">
                                <Link
                                    className="link-livro"
                                    to={book.source === 'catalog' ? `/catalogo/${book.uuid}` : `/google-livros/${book.uuid}`}
                                    title={book.title}
                                >
                                    <img
                                        onError={(e) => { e.currentTarget.src = "/img/book_transparent.png"; }}
                                        src={book.cover_url ? getBookImage(book.cover_url) : "/img/book_transparent.png"}
                                        className="livro"
                                        alt={book.title}
                                    />
                                </Link>
                            </div>
                        ))}
                        {loadingRecs && <BookPlaceholder className="col col-livro" quantity={10} />}
                    </div>

                    {!recommendations?.length && !loadingRecs && (
                        <div className="text-center" style={{ opacity: 0.6 }}>
                            <BookOpenText className="icon-empty" />
                            <h3 className="w-100 text-center user-select-none">
                                Nenhuma recomendação disponível
                            </h3>
                        </div>
                    )}
                </div>
            )}
            
            {activeTab === "dashboard" && userName && (
                <ReadingDashboardPage/>
            )}

            <div ref={endOfListRef} style={{ height: 20 }} />
        </div>
    );
}